package com.swetdot.swetgram.ui.design

import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.animation.core.Easing
import androidx.compose.animation.core.EaseIn
import androidx.compose.animation.core.EaseOut
import androidx.compose.animation.core.FiniteAnimationSpec
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.expandVertically
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.InteractionSource
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.lazy.LazyItemScope
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Modifier
import androidx.compose.ui.Alignment
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.unit.IntOffset
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * The Design System's motion layer -- SDS §19, under §22.6.
 *
 * Everything that moves in Swetgram is built out of this file, for the same reason
 * `SwetgramColors.kt` exists: the alternative to naming these is not "no animation", it is every
 * screen inventing its own duration. §19.2 gives four durations and §19.3 gives two curves; a
 * component that needs a fifth is a component that has misread the spec.
 *
 * Three rules are enforced here rather than left to call sites:
 *
 *  1. **Reduced motion is checked once, centrally.** §22.6 requires it, and a hook that each
 *     screen has to remember to consult is a hook that is missed. [swetTween] collapses to
 *     [snap] when [LocalReduceMotion] is on, so every animation built from it stops moving
 *     without a single `if` at a call site.
 *  2. **No animated value is read during composition where it can be avoided.** The animated
 *     floats here are read inside `graphicsLayer` and `drawBehind` lambdas, which run in the
 *     layer/draw phase -- so a 220ms animation costs zero recompositions instead of thirteen.
 *  3. **Press feedback never disappears; it changes form.** §25.1 requires a visible pressed
 *     state, and §22.6 asks for movement to be replaced rather than deleted. Under reduced
 *     motion the scale becomes an opacity step (see [pressAlphaTarget]).
 */

// -------------------------------------------------------------------------------------------
// Reduced motion (SDS §22.6)
// -------------------------------------------------------------------------------------------

/**
 * Whether the user has asked the system to reduce motion.
 *
 * A `CompositionLocal` keeps previews and tests deterministic while `SwetgramTheme` supplies the
 * live platform preference through [platformReduceMotion]. The platform sources are:
 *
 *  - Android: `Settings.Global.getFloat(resolver, ANIMATOR_DURATION_SCALE, 1f) == 0f`
 *  - iOS: `UIAccessibility.isReduceMotionEnabled` (plus its change notification)
 *
 * Both are `android.*` / platform APIs and cannot live in commonMain, which is the other reason
 * the flag arrives as a value rather than as a lookup.
 */
val LocalReduceMotion = staticCompositionLocalOf { false }

/** Reads the operating system's reduce-motion preference for the current platform. */
@Composable
internal expect fun platformReduceMotion(): Boolean

/** Reads [LocalReduceMotion]. Use this rather than the local, so the source can move. */
@Composable
@ReadOnlyComposable
fun reduceMotion(): Boolean = LocalReduceMotion.current

/**
 * Installs the reduced-motion preference for a subtree.
 *
 * Exists so the wiring is one line in a file this module does not own: wrap the app's content in
 * `ProvideSwetMotion(reduceMotion = platformReduceMotion()) { ... }`.
 */
@Composable
fun ProvideSwetMotion(reduceMotion: Boolean, content: @Composable () -> Unit) {
    CompositionLocalProvider(LocalReduceMotion provides reduceMotion, content = content)
}

// -------------------------------------------------------------------------------------------
// Easing (SDS §19.3)
// -------------------------------------------------------------------------------------------

/**
 * SDS §19.3: "Standard ease-out for enter, standard ease-in for exit."
 *
 * There is deliberately no third curve and no spring. §19.3 also rules out bounce except as
 * intentional playful micro-feedback, and a spring is a bounce whose overshoot nobody chose --
 * which is exactly how Compose's own defaults behave, since `animateItem` and `animate*AsState`
 * both default to `spring()`. Every animation in the app passes one of these two explicitly.
 */
object SwetEasing {
    /** Enter, appear, press-down, expand. */
    val enter: Easing = EaseOut

    /** Exit, disappear, release, collapse. */
    val exit: Easing = EaseIn
}

// -------------------------------------------------------------------------------------------
// Pure decisions. Extracted so they can be tested without a renderer -- see MotionLogicTest.
// -------------------------------------------------------------------------------------------

/**
 * How far a pressed button shrinks.
 *
 * 4%. On a 48dp button that is under 2dp of travel: §25.1 asks for a pressed state that is
 * "visible but subtle", and §2.3's premium minimalism rules out the 8-10% squash that reads as a
 * toy. Small enough that it never disturbs the layout around it, since `graphicsLayer` scales
 * the drawing without re-measuring anything.
 */
internal const val PRESS_SCALE: Float = 0.96f

/**
 * The opacity a pressed control drops to when motion is reduced.
 *
 * §22.6: "Replace movement with fades where appropriate." This is that replacement -- the press
 * still reads, it simply stops moving. It is also, not by accident, how a native iOS control
 * behaves, so the reduced-motion path is not a degraded one.
 */
internal const val PRESS_FADE_ALPHA: Float = 0.72f

/**
 * The pressed state layer, painted in `text.primary` over whatever the surface is.
 *
 * 10% is the level at which the tint is unmistakable on both themes without becoming a second
 * colour. A row or a card does NOT scale: a 72dp full-width row shrinking by 4% moves its edges
 * by three device pixels each, which reads as the list twitching rather than as a press.
 */
internal const val PRESS_STATE_LAYER_ALPHA: Float = 0.10f

/**
 * The scale a badge or dot enters from.
 *
 * A pop, not a bounce (§19.3): `scaleIn` with an ease-out curve approaches 1.0 from below and
 * stops there, with no overshoot.
 */
internal const val POP_INITIAL_SCALE: Float = 0.72f

/** The dimmest point of the skeleton breathe. See [swetSkeletonPulse]. */
internal const val SKELETON_MIN_ALPHA: Float = 0.55f

/**
 * The scale a control is drawn at.
 *
 * Note the `!reduceMotion`: under §22.6 there is no movement at all, and [pressAlphaTarget]
 * takes over. A disabled control gives no feedback in either mode -- pressing it did nothing, so
 * saying otherwise would be a lie the user has to test twice.
 */
internal fun pressScaleTarget(pressed: Boolean, enabled: Boolean, reduceMotion: Boolean): Float =
    if (pressed && enabled && !reduceMotion) PRESS_SCALE else 1f

/** The opacity a control is drawn at. The mirror of [pressScaleTarget]: exactly one applies. */
internal fun pressAlphaTarget(pressed: Boolean, enabled: Boolean, reduceMotion: Boolean): Float =
    if (pressed && enabled && reduceMotion) PRESS_FADE_ALPHA else 1f

/**
 * The alpha of the pressed state layer.
 *
 * No `reduceMotion` parameter, and that is the point: a state layer is a colour change, not
 * movement, so §22.6 has nothing to say about it. What reduced motion removes is the 80ms fade
 * INTO it -- [swetTween] does that, and the layer simply appears instead.
 */
internal fun pressStateLayerAlphaTarget(pressed: Boolean, enabled: Boolean): Float =
    if (pressed && enabled) PRESS_STATE_LAYER_ALPHA else 0f

/** The duration an animation actually runs for, once §22.6 has had its say. */
internal fun motionDurationMillis(durationMillis: Int, reduceMotion: Boolean): Int =
    if (reduceMotion) 0 else durationMillis

// -------------------------------------------------------------------------------------------
// Specs
// -------------------------------------------------------------------------------------------

/**
 * The only animation spec the app should build.
 *
 * [durationMillis] must come from [Motion] -- §19.2 has four values and no others. Under reduced
 * motion this returns [snap], which is how §22.6 is honoured everywhere at once: the animation
 * still exists, it simply arrives at its target on the next frame.
 */
@Composable
@ReadOnlyComposable
fun <T> swetTween(
    durationMillis: Int,
    easing: Easing = SwetEasing.enter,
): FiniteAnimationSpec<T> =
    if (reduceMotion()) snap() else tween(durationMillis = durationMillis, easing = easing)

/** §19.2 `motion.fast` fade in, on §19.3's enter curve. */
@Composable
fun swetFadeIn(durationMillis: Int = Motion.fast): EnterTransition =
    fadeIn(swetTween(durationMillis, SwetEasing.enter))

/** §19.2 `motion.fast` fade out, on §19.3's exit curve. */
@Composable
fun swetFadeOut(durationMillis: Int = Motion.fast): ExitTransition =
    fadeOut(swetTween(durationMillis, SwetEasing.exit))

/**
 * The appearance of a small, discrete thing: an unread badge, an online dot, a status chip.
 *
 * A fade plus a short scale-up. Under reduced motion the scale is dropped and the fade remains,
 * which is §22.6's "replace movement with fades" applied literally.
 */
@Composable
fun swetPopIn(durationMillis: Int = Motion.fast): EnterTransition {
    val spec = swetTween<Float>(durationMillis, SwetEasing.enter)
    return if (reduceMotion()) {
        fadeIn(spec)
    } else {
        fadeIn(spec) + scaleIn(spec, initialScale = POP_INITIAL_SCALE)
    }
}

/** The mirror of [swetPopIn], on §19.3's exit curve. */
@Composable
fun swetPopOut(durationMillis: Int = Motion.fast): ExitTransition {
    val spec = swetTween<Float>(durationMillis, SwetEasing.exit)
    return if (reduceMotion()) {
        fadeOut(spec)
    } else {
        fadeOut(spec) + scaleOut(spec, targetScale = POP_INITIAL_SCALE)
    }
}

/** Expands transient composer context from its top edge; reduced motion keeps only the fade. */
@Composable
fun swetExpandIn(durationMillis: Int = Motion.fast): EnterTransition {
    val spec = swetTween<Float>(durationMillis, SwetEasing.enter)
    return if (reduceMotion()) fadeIn(spec) else {
        fadeIn(spec) + expandVertically(
            animationSpec = swetTween(durationMillis, SwetEasing.enter),
            expandFrom = Alignment.Top,
        )
    }
}

/** Collapses transient composer context toward its top edge; reduced motion keeps only the fade. */
@Composable
fun swetCollapseOut(durationMillis: Int = Motion.fast): ExitTransition {
    val spec = swetTween<Float>(durationMillis, SwetEasing.exit)
    return if (reduceMotion()) fadeOut(spec) else {
        fadeOut(spec) + shrinkVertically(
            animationSpec = swetTween(durationMillis, SwetEasing.exit),
            shrinkTowards = Alignment.Top,
        )
    }
}

// -------------------------------------------------------------------------------------------
// Press feedback (SDS §19.4 "Button press feedback", §25.1)
// -------------------------------------------------------------------------------------------

/** Which of §25.1's two pressed treatments a component uses. See [swetPressable]. */
enum class SwetPressFeedback {
    /** For things that read as objects: buttons, icon buttons, the send and attach actions. */
    Scale,

    /** For things that read as surfaces: list rows, cards, tab items. */
    StateLayer,

    /** For a control that draws its own pressed state. */
    None,
}

/**
 * Shrinks the content by [PRESS_SCALE] while it is held, over §19.2's `motion.instant`.
 *
 * 80ms because §19.2 gives that token to "tiny feedback" and because anything longer is felt as
 * lag rather than as response -- §2.1 puts the perception of speed above everything else.
 *
 * The animated float is read inside `graphicsLayer`, so the press costs no recomposition at all;
 * only the layer's transform changes. Reading `scale.value` here in composition instead would
 * recompose the button on every one of the five frames.
 */
@Composable
fun Modifier.swetPressScale(
    interactionSource: InteractionSource,
    enabled: Boolean = true,
): Modifier {
    val pressed by interactionSource.collectIsPressedAsState()
    val reduced = reduceMotion()
    val spec = swetTween<Float>(Motion.instant, SwetEasing.enter)
    val scale = animateFloatAsState(
        targetValue = pressScaleTarget(pressed, enabled, reduced),
        animationSpec = spec,
        label = "swetPressScale",
    )
    val dim = animateFloatAsState(
        targetValue = pressAlphaTarget(pressed, enabled, reduced),
        animationSpec = spec,
        label = "swetPressDim",
    )
    return this.graphicsLayer {
        scaleX = scale.value
        scaleY = scale.value
        alpha = dim.value
    }
}

/**
 * Tints the surface under the content while it is held, over §19.2's `motion.instant`.
 *
 * The layer is a plain rectangle. Rounded components must `clip` BEFORE this modifier -- every
 * one in the design system already does, because they clip before their click handler anyway so
 * that the press does not paint over their corners.
 *
 * `text.primary` rather than a new token: it is white on dark and near-black on light, which is
 * what a state layer has to be in each theme, and §4.8 does not permit inventing a colour for it.
 */
@Composable
fun Modifier.swetPressStateLayer(
    interactionSource: InteractionSource,
    enabled: Boolean = true,
): Modifier {
    val pressed by interactionSource.collectIsPressedAsState()
    val layerColor = SwetgramThemeTokens.colors.textPrimary
    val alpha = animateFloatAsState(
        targetValue = pressStateLayerAlphaTarget(pressed, enabled),
        animationSpec = swetTween(Motion.instant, SwetEasing.enter),
        label = "swetPressStateLayer",
    )
    // `drawBehind` puts the read in the draw phase, and paints over whatever `background` this
    // modifier follows while staying underneath the row's own text and avatar.
    return this.drawBehind {
        val value = alpha.value
        if (value > 0f) drawRect(color = layerColor, alpha = value)
    }
}

/**
 * A click WITH the design system's pressed state, replacing `Modifier.clickable`.
 *
 * `indication = null` is the load-bearing argument. Material3's `MaterialTheme` installs its
 * ripple as `LocalIndication`, so a bare `clickable` inherits an expanding-circle animation that
 * no part of the SDS asks for, whose duration is Material's and not §19.2's, and which lands
 * differently on a 44dp icon button than on a 72dp row. Turning it off and drawing §25.1's
 * pressed state explicitly is what makes the feedback identical on Android and iOS -- which is
 * the entire job §23.1 gives this document.
 *
 * The interaction source is created here by default so a call site cannot forget to pass one and
 * silently lose the feedback; pass your own when the same press must drive something else too.
 */
@Composable
fun Modifier.swetPressable(
    onClick: () -> Unit,
    enabled: Boolean = true,
    role: Role? = Role.Button,
    onClickLabel: String? = null,
    feedback: SwetPressFeedback = SwetPressFeedback.StateLayer,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
): Modifier {
    val feedbackModifier = when (feedback) {
        SwetPressFeedback.Scale -> Modifier.swetPressScale(interactionSource, enabled)
        SwetPressFeedback.StateLayer -> Modifier.swetPressStateLayer(interactionSource, enabled)
        SwetPressFeedback.None -> Modifier
    }
    return this
        .then(feedbackModifier)
        .clickable(
            interactionSource = interactionSource,
            indication = null,
            enabled = enabled,
            onClickLabel = onClickLabel,
            role = role,
            onClick = onClick,
        )
}

/**
 * Keeps an outgoing transition layer visual while immediately removing all interaction from it.
 * Alpha does not affect hit testing in Compose, so every overlapping transition must explicitly
 * disable its old subtree.
 */
fun Modifier.swetTransitionTarget(active: Boolean): Modifier {
    if (active) return this
    return this
        .focusProperties { canFocus = false }
        .clearAndSetSemantics { }
        .pointerInput(Unit) {
            awaitPointerEventScope {
                while (true) {
                    awaitPointerEvent(PointerEventPass.Initial).changes.forEach { it.consume() }
                }
            }
        }
}

// -------------------------------------------------------------------------------------------
// Appearance
// -------------------------------------------------------------------------------------------

/**
 * Fades the content in once, when it first enters the composition.
 *
 * For things a screen swaps in with a plain `if`: an error banner, a loading state, an empty
 * state. `AnimatedVisibility` would be the better tool if the caller could keep the node around
 * for the exit, and where it can, it should -- this is for the very common case where it cannot.
 *
 * NOT for message bubbles or list rows. The content is invisible on the frame it is added, and
 * §2.1 does not allow anything to stand between the user and the message they just sent. Use
 * [swetAnimateMessageItem], which starts at full opacity.
 */
@Composable
fun Modifier.swetAppear(durationMillis: Int = Motion.fast): Modifier {
    if (reduceMotion()) return this
    var shown by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) { shown = true }
    val alpha = animateFloatAsState(
        targetValue = if (shown) 1f else 0f,
        animationSpec = swetTween(durationMillis, SwetEasing.enter),
        label = "swetAppear",
    )
    return this.graphicsLayer { this.alpha = alpha.value }
}

// -------------------------------------------------------------------------------------------
// Lazy list items (SDS §19.4)
// -------------------------------------------------------------------------------------------

/**
 * Appearance, removal and reordering for one row of a `LazyColumn`.
 *
 * §19.4 lists "Every list item on normal scrolling" under what motion must NOT be used for, and
 * this respects that literally: `animateItem` fires on DATA changes only -- an item added,
 * removed, or moved -- and never while the user is scrolling past items that have not changed.
 *
 * Requires a stable `key` on the `items()` call. Without one Compose cannot tell an insertion
 * from a recycle, and the list will fade rows in and out as it scrolls, which is precisely the
 * thing §19.4 forbids. `ChatListScreen` already keys on the chat id.
 *
 * Durations: `motion.fast` (150ms) for the fades, because an item arriving is §19.2's "state
 * change", and `motion.normal` (220ms) for the reorder, because the row travels a real distance
 * and 150ms across half a screen reads as a jump.
 */
@Composable
fun LazyItemScope.swetAnimateItem(): Modifier {
    if (reduceMotion()) return Modifier
    return Modifier.animateItem(
        fadeInSpec = tween(durationMillis = Motion.fast, easing = SwetEasing.enter),
        placementSpec = tween<IntOffset>(durationMillis = Motion.normal, easing = SwetEasing.enter),
        fadeOutSpec = tween(durationMillis = Motion.fast, easing = SwetEasing.exit),
    )
}

/**
 * The same, for the message timeline -- with the fade-IN deliberately removed.
 *
 * §19.4 asks for a "Message appear animation" and this is it: the bubble is at full opacity on
 * the frame it is added, and what animates is everything else in the timeline sliding up to make
 * room for it. Fading a message in would mean the sender watches their own words resolve out of
 * nothing for 150ms, which §2.1 ("prioritize instant interaction feedback") rules out and which
 * is worse on a slow device, not better.
 *
 * The fade OUT is kept: a deleted message leaving is not something the user is waiting on.
 */
@Composable
fun LazyItemScope.swetAnimateMessageItem(): Modifier {
    if (reduceMotion()) return Modifier
    return Modifier.animateItem(
        fadeInSpec = null,
        placementSpec = tween<IntOffset>(durationMillis = Motion.normal, easing = SwetEasing.enter),
        fadeOutSpec = tween(durationMillis = Motion.fast, easing = SwetEasing.exit),
    )
}

// -------------------------------------------------------------------------------------------
// Loading
// -------------------------------------------------------------------------------------------

/**
 * The skeleton breathe: opacity easing between 1.0 and [SKELETON_MIN_ALPHA] and back.
 *
 * This is a considered reversal of the note that used to sit on `SwetSkeleton`, which refused any
 * skeleton motion by citing §19.4's ban on "Decorative loops". The distinction being drawn now is
 * that a skeleton is not decoration: §25 lists Loading as a required component state and §29 will
 * not pass a screen without one, and a grid of motionless grey bars is indistinguishable from a
 * screen that has finished loading and rendered wrong. The pulse is the only thing telling the
 * user the app is still working. It stops the moment real content replaces it, so it is bounded
 * by the request rather than running forever the way a decorative loop does.
 *
 * A travelling shimmer would be the decorative version, and is still refused.
 *
 * [Motion.skeletonPulse] is 3 × `motion.slow` in each direction. §19.2's tokens are transition
 * lengths and none of them describes a resting rhythm -- at 300ms a skeleton strobes. Deriving it
 * from the scale keeps it on the same arithmetic instead of introducing a fifth free number.
 *
 * `RepeatMode.Reverse` plays §19.3's enter curve backwards on the way down, which is its exit
 * curve, so the breathe is ease-out then ease-in without stating a second spec.
 *
 * The value is read inside `graphicsLayer`: this animation runs for as long as the network is
 * slow, and reading it in composition would recompose the skeleton sixty times a second for the
 * whole of that -- the one thing the brief rules out absolutely.
 */
@Composable
fun Modifier.swetSkeletonPulse(): Modifier {
    // §22.6: this is a non-essential transition, and the block still reserves its space. Reduced
    // motion gets the static skeleton the app had before.
    if (reduceMotion()) return this

    val transition = rememberInfiniteTransition(label = "swetSkeleton")
    val alpha = transition.animateFloat(
        initialValue = 1f,
        targetValue = SKELETON_MIN_ALPHA,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = Motion.skeletonPulse, easing = SwetEasing.enter),
            repeatMode = RepeatMode.Reverse,
        ),
        label = "swetSkeletonAlpha",
    )
    return this.graphicsLayer { this.alpha = alpha.value }
}
